import { lazy, Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";
import AppLayout from "app/layout/AppLayout";
import sessionRoutes from "app/routes/sessionRoutes";
import { DropdownProvider } from "app/contexts/DropdownContext";

// Every page is code-split; AppLayout's <Suspense> shows the loading state.
// URL paths are unchanged from the original app (some are misspelt, but they are bookmarked).

// Jobs & tasks
const JobList = lazy(() => import("app/pages/jobs/JobList"));
const CreateJobs = lazy(() => import("app/pages/jobs/CreateJobs"));
const EditJob = lazy(() => import("app/pages/jobs/EditJob"));
const JobSectionPopout = lazy(() => import("app/pages/jobs/JobSectionPopout"));
const TaskList = lazy(() => import("app/pages/tasks/TaskList"));
const CreateTask = lazy(() => import("app/pages/tasks/CreateTask"));
// Documents
const UploadDocument = lazy(() => import("app/pages/documents/UploadDocument"));
const DocumentList = lazy(() => import("app/pages/documents/DocumentList"));
const UpdateDocument = lazy(() => import("app/pages/documents/UpdateDocument"));
const UpdateExistingDocument = lazy(() => import("app/pages/documents/UpdateExistingDocument"));
const DeletedDocumentList = lazy(() => import("app/pages/documents/DeletedDocumentList"));
// Requests & queries
const RequestList = lazy(() => import("app/pages/requests/RequestList"));
const AddRequest = lazy(() => import("app/pages/requests/AddRequest"));
const EditRequest = lazy(() => import("app/pages/requests/EditRequest"));
const QueryList = lazy(() => import("app/pages/queries/QueryList"));
// Users & admin
const UserList = lazy(() => import("app/pages/users/UserList"));
const RoleManagement = lazy(() => import("app/pages/users/RoleManagement"));
const AdminPage = lazy(() => import("app/pages/admin/AdminPage"));
// Dashboard & reports
const Dashboard = lazy(() => import("app/pages/dashboard/Dashboard"));
const ProjectOverview = lazy(() => import("app/pages/reports/ProjectOverview"));
const UserEntries = lazy(() => import("app/pages/reports/UserEntries"));
const DailyStatusReport = lazy(() => import("app/pages/reports/DailyStatusReport"));
const ProjVsRevReport = lazy(() => import("app/pages/reports/ProjVsRevReport"));
const ProjVsAchReport = lazy(() => import("app/pages/reports/ProjVsAchReport"));
const DailyUserProdReport = lazy(() => import("app/pages/reports/DailyUserProdReport"));
const InputsReport = lazy(() => import("app/pages/reports/InputsReport"));
const InputsSummaryReport = lazy(() => import("app/pages/reports/InputsSummaryReport"));
const OutstandingTaskReport = lazy(() => import("app/pages/reports/OutstandingTaskReport"));
const OutstandingSummaryReport = lazy(() => import("app/pages/reports/OutstandingSummaryReport"));
const OutputsReport = lazy(() => import("app/pages/reports/OutputsReport"));
const OutputsByWorkDriverReport = lazy(() => import("app/pages/reports/OutputsByWorkDriverReport"));
const WeekWiseAnalysisReport = lazy(() => import("app/pages/reports/WeekWiseAnalysisReport"));
const QueryReport = lazy(() => import("app/pages/reports/QueryReport"));
const QueriesSummaryReport = lazy(() => import("app/pages/reports/QueriesSummaryReport"));
const TeamDailyStatusReport = lazy(() => import("app/pages/reports/TeamDailyStatusReport"));

// Only signed-in users (login stores sessionStorage["userdata"]) reach the app shell.
const ProtectedRoute = () =>
  sessionStorage.getItem("userdata") ? <Outlet /> : <Navigate to="/session/signin" replace />;

const appRoutes = [
  { path: "/viewjobs", element: <JobList /> },
  { path: "/uploadjobs", element: <CreateJobs /> },
  { path: "/material/editjob", element: <EditJob /> },
  { path: "/viewtasks", element: <TaskList /> },
  { path: "/createtask", element: <CreateTask /> },

  { path: "/convertdoc", element: <UploadDocument /> },
  { path: "/doctable", element: <DocumentList /> },
  { path: "/updatedocument", element: <UpdateDocument /> },
  { path: "/existupdatedocument", element: <UpdateExistingDocument /> },
  { path: "/deletedocumets", element: <DeletedDocumentList /> },

  { path: "/requests", element: <RequestList /> },
  { path: "/addrequest", element: <AddRequest /> },
  { path: "/editrequest", element: <EditRequest /> },
  { path: "/queries", element: <QueryList /> },

  { path: "/userdetails", element: <UserList /> },
  { path: "/roles", element: <RoleManagement /> },
  { path: "/admin", element: <AdminPage /> },

  { path: "/dashboard", element: <Dashboard /> },
  { path: "/projectstatus", element: <ProjectOverview /> },
  { path: "/userentries", element: <UserEntries /> },
  { path: "/dailystatus", element: <DailyStatusReport /> },
  { path: "/projvsrevreport", element: <ProjVsRevReport /> },
  { path: "/projvsachreport", element: <ProjVsAchReport /> },
  { path: "/dailyuserprodreport", element: <DailyUserProdReport /> },
  { path: "/inputsreport", element: <InputsReport /> },
  { path: "/inputssummery", element: <InputsSummaryReport /> },
  { path: "/outstandingtask", element: <OutstandingTaskReport /> },
  { path: "/outstandsummery", element: <OutstandingSummaryReport /> },
  { path: "/outputsreport", element: <OutputsReport /> },
  { path: "/outputsreportworkdriver", element: <OutputsByWorkDriverReport /> },
  { path: "/outputsreportgrapg", element: <WeekWiseAnalysisReport /> },
  { path: "/requestreport", element: <QueryReport /> },
  { path: "/queriesreport", element: <QueriesSummaryReport /> },
  { path: "/teamDailySatus", element: <TeamDailyStatusReport /> },
];

const routes = [
  { path: "/", element: <Navigate to="session/signin" /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: (
          <DropdownProvider>
            <AppLayout />
          </DropdownProvider>
        ),
        children: appRoutes,
      },
      // Edit Job → Tasks / Documents "Pop out": own window, no nav bars (still signed-in only)
      ...[["/jobtasks", "tasks"], ["/jobdocs", "docs"]].map(([path, section]) => ({
        path,
        element: (
          <DropdownProvider>
            <Suspense fallback={null}>
              <JobSectionPopout section={section} />
            </Suspense>
          </DropdownProvider>
        ),
      })),
    ],
  },
  ...sessionRoutes,
];

export default routes;
